import { useCallback } from 'react'
import { useChatStore } from '@/store/chatStore'
import { sendMessage } from '@/services/signalr'
import { askQuestion, updateSessionTitle } from '@/services/api'
import type { AskQuestionRequest, RetrievedChunk } from '@/types'
import type * as signalR from '@microsoft/signalr'

// ─────────────────────────────────────────────
// useChat
// Primary hook for sending messages.
// Strategy:
//   1. Try SignalR streaming (token-by-token)
//   2. If SignalR unavailable → REST fallback
//      (full response returned at once)
// All state updates go through chatStore.
// ─────────────────────────────────────────────

interface UseChatOptions {
  connection: signalR.HubConnection | null
  isConnected: boolean
}

export function useChat({ connection, isConnected }: UseChatOptions) {
  const {
    config,
    currentSessionId,
    addUserMessage,
    addAssistantPlaceholder,
    appendToken,
    finalizeMessage,
    setCurrentSession,
    addSession,
    updateSessionTitle: updateTitleInStore,
    setIsStreaming,
    setWarningMessage,
    startNewChat,
    saveActiveMessages,
  } = useChatStore()

  const sendChat = useCallback(async (userInput: string) => {
    if (!config || !userInput.trim()) return

    // 1. Add user message to UI immediately — optimistic update
    addUserMessage(userInput.trim())

    // 2. Add empty assistant placeholder — tokens stream into this
    const placeholder = addAssistantPlaceholder()

    setIsStreaming(true)
    setWarningMessage(null)

    const request: AskQuestionRequest = {
      tenantId:           useChatStore.getState().tenantId,          // ← Guid from auth response, not slug
      domainId:           config.domainId,
      sessionId:          currentSessionId ?? undefined,
      message:            userInput.trim(),
      topK:               5,
      minScore:           0.3,  // ← lowered from 0.7 — real embeddings score 0.3-0.8 range
      maxHistoryMessages: 20,
      responseLanguage:   useChatStore.getState().selectedLanguage,  // ← always fresh — avoids stale closure on chips and Samsung keyboard
    }

    // ── Strategy 1: SignalR streaming ────────────
    if (isConnected && connection) {
      await streamViaSignalR(request, placeholder.id)
      return
    }

    // ── Strategy 2: REST fallback ─────────────────
    await fetchViaRest(request, placeholder.id)

  }, [config, currentSessionId, isConnected, connection])

  // ── SignalR streaming path ────────────────────
  async function streamViaSignalR(
    request: AskQuestionRequest,
    placeholderId: string
  ) {
    return new Promise<void>((resolve) => {
      sendMessage(
        connection!,
        request,
        // onToken — append each streamed token to the placeholder message
        (token: string) => {
          appendToken(placeholderId, token)
        },
       // onComplete — stream finished, sessionId returned from hub
        (sessionId: string) => {
          finalizeMessage(placeholderId)
          setIsStreaming(false)

          // Register new session if this was the first message
          if (sessionId && sessionId !== currentSessionId) {
            setCurrentSession(sessionId)
            const autoTitle = request.message.slice(0, 60)
            addSession({
              id:              sessionId,
              title:           autoTitle,
              domainId:        request.domainId,
              createdAt:       new Date(),
              totalTokensUsed: 0,
            })
          }

          // Save active messages snapshot so "back to current chat" can restore them
          saveActiveMessages()
          resolve()
        },
        // onError — SignalR stream failed mid-way (not trial block — that comes via ReceiveToken)
          (error: string) => {
            console.error('[SignalR] Stream error:', error)
            finalizeMessage(placeholderId)
            setIsStreaming(false)
            resolve()
          }
      )
    })
  }

  // ── REST fallback path ────────────────────────
  async function fetchViaRest(
    request: AskQuestionRequest,
    placeholderId: string
  ) {
    try {
      const response = await askQuestion(request)

      // Persist new sessionId returned by backend
      if (response.sessionId && response.sessionId !== currentSessionId) {
        setCurrentSession(response.sessionId)

        // Auto-generate session title from first user message (truncated)
        const autoTitle = request.message.slice(0, 60)
        addSession({
          id:               response.sessionId,
          title:            autoTitle,
          domainId:         request.domainId,
          createdAt:        new Date(),
          totalTokensUsed:  response.totalTokens,
        })

        // Persist title to backend async — don't await (non-blocking)
        updateSessionTitle(response.sessionId, autoTitle).catch(() => {})
        updateTitleInStore(response.sessionId, autoTitle)
      }

      // Attach full response to placeholder
      finalizeMessage(
        placeholderId,
        response.retrievedChunks as RetrievedChunk[]
      )

      // Show warning if trial expired and Ollama fallback is active
      if (response.warningMessage) {
        setWarningMessage(response.warningMessage)
      }

    } catch (err) {
      console.error('[REST] Chat request failed:', err)
      finalizeMessage(placeholderId)
    } finally {
      setIsStreaming(false)
    }
  }

  return {
    sendChat,
    startNewChat,
  }
}